<template>
  <div class="access-event-card__container">
    <div class="access-event-card__high-level-info-container">
      <div class="access-event-card__tap-details">
        <div class="access-event-card__header">
          {{ formattedDate(accessEvent.eventTimestamp) }}
        </div>
        <div class="access-event-card__subheader">
          {{ accessEvent.accessPointDescription }}
        </div>
        <div
          v-if="hasDetections"
          class="access-event-card__buttons"
         >
          <owal-banner-button
            class="access-event-card__info-panel-button"
            type="static"
            title="More Detections"
            accessory-type="none"
            identifier="button"
            alignment="center"
            @click="toggleAdditionalDetectionPanel"
          />
          <owal-banner-button
            class="access-event-card__info-panel-button"
            type="static"
            title="Confirm Detections"
            accessory-type="none"
            identifier="button"
            alignment="center"
            @click="showModalTaggingPanel"
          />
          <owal-banner-button
            class="access-event-card__info-panel-button"
            type="static"
            title="Watch Video"
            :url="urlForVideo(accessEvent)"
            accessory-type="none"
            identifier="button"
            alignment="center"
          />
        </div>
        <div v-else class="access-event-card__buttons">
          <owal-banner-button
            class="access-event-card__info-panel-button"
            style="min-width: 7rem"
            type="static"
            title="Watch Video"
            :url="urlForVideo(accessEvent)"
            accessoryType="none"
            identifier="button"
            alignment='center'
            />
          </div>
        </div>
        <access-event-match
          v-if="hasDetections"
          :detectionId="mostRelevantDetection.detectionId"
          :detection="mostRelevantDetection"
          :bestMatchSynopsis="titleCase(mostRelevantDetection.matchConfidenceLabel)"
          :verified='verified'
          :isCotenant='mostRelevantDetection.isCotenant'
          :imageUrl="mostRelevantDetection.imageUrl"
          />
        </div>
        <div class="access-event-card__additional-match-container" v-if="displayAdditionalDetections">
          <div
            v-for="detection in mappedAccessEventDetections"
            :key="detection.detectionId"
            style="margin: 0 0.35rem;"
            >
            <access-event-match
              :detectionId='detection.detectionId'
              :detection="detection"
              :bestMatchSynopsis='titleCase(detection.matchConfidenceLabel)'
              :showVerified='false'
              :isCotenant='detection.isCotenant'
              :imageUrl='detection.imageUrl'
              />
          <div/>
        <div/>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { OwalBannerButton } from '@/libraries/owal-ui/'
import { mapGetters } from 'vuex'
import AccessEventMatch from './AccessEventMatch'
import { titleCase } from '@/libraries/util/string'
import { imageForDetectionID } from '@/libraries/api/faces'

export default {
  name: 'AccessEventCard',
  components: { OwalBannerButton, AccessEventMatch },
  props: {
    accessEvent: {
      type: Object,
    },
    hasDetections: {
      type: Boolean,
    },
  },
  data () {
    return {
      displayAdditionalDetections: false,
    }
  },
  computed: {
    ...mapGetters({
      taggingPanelVisible: 'credentialMismatch/taggingPanelVisible',
      currentDomainUUID: 'currentDomainUUID',
    }),
    verified () {
      let by, at

      if (this.accessEvent && this.accessEvent.lastVerifiedTenantDescription) {
        by = this.accessEvent.lastVerifiedTenantDescription.split(' ')[0]
      }

      if (this.accessEvent.lastVerifiedTimestamp) {
        at = this.lastVerifiedTimeString(this.accessEvent.lastVerifiedTimestamp)
      }

      return {
        at,
        by,
      }
    },
    showVerifiedForAccessEvent () {
      if (!this.accessEvent) {
        return false
      }
      return this.accessEvent.confirmedDetections !== null
    },
    mappedAccessEventDetections () {
      const mapped = this.accessEvent.detections.map(detection => {
        return this.mapAccessEventDetectionToMatch(detection)
      })
      return mapped
    },
    mostRelevantDetection () {
      const detection = this.accessEvent.detections[0]
      const mostRelevantDetection = this.mapAccessEventDetectionToMatch(detection)
      if (this.accessEvent.confirmedDetections.length) {
        const validMatches = this.accessEvent.confirmedDetections.filter(detection => detection.rejected !== true)
        if (!validMatches.length) {
          mostRelevantDetection.matchConfidenceLabel = 'Low'
          mostRelevantDetection.isCotenant = false
          mostRelevantDetection.isAbsent = true
        }
      }
      return mostRelevantDetection
    },
  },
  methods: {
    titleCase,
    formattedDate (date) {
      return moment.utc(date).local().format('LLLL') // 'MMMM Do, HH:mm:ss')
    },
    lastVerifiedTimeString (ts) {
      let fromNowString = moment.utc(ts).fromNow()
      if (fromNowString === 'a day ago') {
        fromNowString = 'yesterday'
      }
      if (fromNowString === 'in a few seconds') {
        fromNowString = 'just now'
      }
      return fromNowString
    },
    toggleAdditionalDetectionPanel () {
      this.displayAdditionalDetections = !this.displayAdditionalDetections
    },
    showModalTaggingPanel () {
      this.$store.commit('credentialMismatch/setSelectedAccessEvent', this.accessEvent)
      this.$store.commit('credentialMismatch/setTaggingPanelVisibility', !this.taggingPanelVisible)
    },
    urlForVideo () {
      let url = `https://web.owal.io/view/${this.accessEvent.cameraUUID}/${this.accessEvent.eventTimestamp.replace(' ', 'T')}Z`
      return url
    },
    mapAccessEventDetectionToMatch (detection) {
      let matchConfidences = {}
      if (detection.matchConfidences) {
        matchConfidences.nn_prob = detection.isCotenant ? detection.cotenantMatchConfidences.nn_prob : detection.matchConfidences.nn_prob
      } else {
        matchConfidences.face_detector_conf = detection.faceDetectorConfidence
      }
      const accessEventMatchObject = {
        isCotenant: detection.isCotenant,
        detectionId: detection.detectionId,
        matchConfidences,
        matchConfidenceLabel: detection.isCotenant ? detection.cotenantMatchConfidenceLabel.label : detection.matchConfidenceLabel,
        score: detection.score,
        imageUrl: imageForDetectionID(detection.detectionId),
      }
      return accessEventMatchObject
    },
  },
}
</script>

<style scoped>
@media only screen and (min-width: 768px) {
  .owal.modal.form.style .owal.panel.active.window,
  .owal.modal.form.style .owal.panel.inactive.window {
    max-width: 50rem;
    max-height: 80%;
  }
}

.access-event-card__additional-match-container {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
}

.access-event-card__info-panel-button {
  align-self: center;
  text-align: center;
  margin: 0.5rem 0.5rem;
}

.access-event-card__tap-details {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  justify-content: center;
  align-items: flex-start;
  height: 10rem;
}

.access-event-card__high-level-info-container {
  display: flex;
  flex-direction: row;
  align-content: center;
  justify-content: space-between;
  align-items: flex-start;
}

.access-event-card__container {
  border-radius: 0.5rem;
  background-color: rgba(216, 216, 216, 0.25);
  padding: 1.5rem;
  margin: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
}

.access-event-card__header {
  font-size: 1.75rem;
  line-height: 1.75rem;
  margin: 0;
  font-family: 'Lato-Bold', 'Helvetica-Bold', 'Helvetica Bold', 'Helvetica';
}

.access-event-card__subheader {
  color: #69686a;
  font-size: 1rem;
  line-height: 1.3rem;
  font-family: 'Lato', 'Helvetica';
  font-style: oblique;
  max-width: 41.4285714286rem;
  margin: 0.5rem 0 1rem 0;
}

.access-event-card__buttons {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  max-width: 100%;
}

*.icon {
  height: 13px;
  width: 13px;
  margin: 0 6px 0 5px;
  background-size: 100%;
  background-color: none;
  background-repeat: no-repeat;
  background-position: center;
  overflow: visible;
}
</style>
